<template>
  <header class="sticky top-0 z-40 border-b border-slate-200 bg-white">
    <div class="flex h-[4.25rem] items-center gap-2 px-3 sm:gap-3 sm:px-6">
      <button
        class="grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-700 transition hover:bg-slate-100 sm:h-10 sm:w-10 lg:hidden"
        type="button"
        :aria-expanded="mobileSidebarOpen"
        aria-label="Open menu"
        @click="mobileSidebarOpen = true"
      >
        <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true">
          <path d="M4 7h16" />
          <path d="M4 12h16" />
          <path d="M4 17h16" />
        </svg>
      </button>

      <router-link class="inline-flex min-w-0 shrink items-center" to="/explore" aria-label="NetFlyr home">
        <img class="h-7 w-auto max-w-[6.5rem] object-contain sm:h-8 sm:max-w-none" src="/logo.svg" alt="NetFlyr" />
      </router-link>

      <NavbarSearchLocation
        :search="search"
        :display-location="displayLocation"
        :suggestions="searchSuggestions"
        :loading="searchLoading"
        @update:search="search = $event"
        @submit-search="submitSearch"
        @open-location="openLocationModal"
        @select-suggestion="selectSearchSuggestion"
      />

      <router-link
        class="nf-primary-button nf-primary-button--compact ml-auto shrink-0"
        to="/flyers/create"
      >
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true">
          <path d="M12 5v14" />
          <path d="M5 12h14" />
        </svg>
        <span>Post</span>
      </router-link>

      <router-link
        v-if="session.isLoggedIn"
        class="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-700 transition hover:bg-blue-50 hover:text-blue-700 sm:h-10 sm:w-10"
        active-class="bg-blue-50 text-blue-700"
        to="/chats"
        aria-label="Open chats"
        title="Chats"
      >
        <i class="ri-chat-3-line text-lg sm:text-xl" aria-hidden="true"></i>
      </router-link>

      <template v-if="session.isLoggedIn">
        <div ref="accountDropdownEl" class="relative shrink-0">
          <button
            class="inline-flex h-9 shrink-0 items-center gap-1 rounded-full bg-slate-100 p-1 text-sm font-semibold text-slate-950 transition hover:bg-slate-200 sm:h-10 sm:gap-2 sm:pl-2 sm:pr-3"
            :class="isBusinessAccount ? 'bg-blue-50 ring-1 ring-blue-200 hover:bg-blue-100' : ''"
            type="button"
            :aria-expanded="accountDropdownOpen"
            aria-haspopup="menu"
            @click="accountDropdownOpen = !accountDropdownOpen"
          >
            <span class="relative shrink-0">
              <img v-if="profileImage" class="h-7 w-7 rounded-full object-cover ring-1 ring-slate-200" :src="profileImage" alt="" />
              <span v-else class="grid h-7 w-7 place-items-center rounded-full bg-blue-700 text-xs font-semibold text-white">
                {{ accountInitial }}
              </span>
              <span
                v-if="isBusinessAccount"
                class="absolute bottom-[-2px] right-[-3px] grid h-4 w-4 place-items-center rounded-full bg-blue-600 text-white shadow-sm ring-2 ring-white"
                aria-hidden="true"
              >
                <svg class="h-2.5 w-2.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M2.5 6.25h11" />
                  <path d="M3.25 6.25v6.25h9.5V6.25" />
                  <path d="M2.25 6.25 3.4 3.5h9.2l1.15 2.75" />
                  <path d="M6.25 12.5V9h3.5v3.5" />
                </svg>
              </span>
            </span>
            <span class="hidden max-w-[9rem] truncate sm:inline">{{ accountLabel }}</span>
            <svg class="hidden h-4 w-4 text-slate-500 sm:block" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" aria-hidden="true">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>

          <div
            v-if="accountDropdownOpen"
            class="absolute right-0 top-[calc(100%+0.5rem)] z-50 grid min-w-[13rem] overflow-hidden rounded-2xl border border-slate-200 bg-white py-2 shadow-xl shadow-slate-950/10"
            role="menu"
          >
            <div v-if="isBusinessAccount" class="mx-2 mb-2 flex items-center gap-3 rounded-xl bg-blue-50 px-3 py-2.5">
              <span class="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-blue-600 text-white">
                <i class="ri-store-2-line text-lg" aria-hidden="true"></i>
              </span>
              <span class="min-w-0">
                <strong class="block truncate text-sm font-semibold text-slate-950">{{ accountLabel }}</strong>
                <span class="block text-xs font-medium text-blue-700">Business account</span>
              </span>
            </div>
            <div v-if="isBusinessAccount" class="mb-1 border-t border-slate-100"></div>
            <router-link
              v-for="item in accountDropdownLinks"
              :key="item.to"
              class="flex items-center gap-2 px-4 py-2.5 text-sm font-normal text-slate-700 transition hover:bg-slate-50 hover:text-slate-950"
              :to="item.to"
              @click="closeAccountDropdown"
            >
              <i class="text-base text-slate-500" :class="item.icon" aria-hidden="true"></i>
              <span>{{ item.label }}</span>
            </router-link>
            <div class="my-1 border-t border-slate-100"></div>
            <button class="px-4 py-2.5 text-left text-sm font-medium text-blue-700 transition hover:bg-blue-50 hover:text-blue-800" type="button" @click="logout">
              Logout
            </button>
          </div>
        </div>
      </template>
      <template v-else>
        <router-link class="inline-flex h-10 shrink-0 items-center rounded-full bg-slate-100 px-4 text-sm font-semibold text-slate-950 transition hover:bg-slate-200" to="/login">
          Login
        </router-link>
      </template>
    </div>
  </header>

  <aside
    class="fixed bottom-0 left-0 top-[4.25rem] z-30 hidden border-r border-slate-200 bg-white transition-[width] duration-200 lg:block"
    :class="desktopSidebarExpanded ? 'w-60' : 'w-20'"
    aria-label="Sidebar navigation"
  >
    <button
      class="absolute -right-3 top-5 z-10 grid h-4 w-4 place-items-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-md transition hover:bg-slate-100 hover:text-slate-950"
      type="button"
      :aria-label="desktopSidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'"
      :title="desktopSidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'"
      @click="toggleDesktopSidebar"
    >
      <svg
        class="h-3.5 w-3.5 transition-transform"
        :class="desktopSidebarExpanded ? '' : 'rotate-180'"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m15 18-6-6 6-6" />
      </svg>
    </button>
    <SidebarContent
      :links="visibleLinks"
      :account-label="accountLabel"
      :is-logged-in="session.isLoggedIn"
      :collapsed="!desktopSidebarExpanded"
      :is-explore-route="isExploreRoute"
      :explore-sections="exploreSections"
      :active-content-type="activeContentType"
    />
  </aside>

  <div v-if="mobileSidebarOpen" class="fixed inset-0 z-50 lg:hidden">
    <button class="absolute inset-0 bg-slate-950/40" type="button" aria-label="Close menu" @click="mobileSidebarOpen = false" />
    <aside class="relative h-full w-72 max-w-[86vw] border-r border-slate-200 bg-white shadow-2xl shadow-slate-950/20">
      <SidebarContent
        :links="visibleLinks"
        :account-label="accountLabel"
        :is-logged-in="session.isLoggedIn"
        :is-explore-route="isExploreRoute"
        :explore-sections="exploreSections"
        :active-content-type="activeContentType"
        mobile
        @close="mobileSidebarOpen = false"
      />
    </aside>
  </div>

  <div v-if="locationModalOpen" class="fixed inset-0 z-50 grid place-items-center overflow-y-auto bg-slate-950/55 px-3 py-4 sm:px-4 sm:py-6" role="dialog" aria-modal="true" aria-label="Choose location">
    <div class="flex max-h-[calc(100svh-2rem)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl shadow-slate-950/30 sm:max-h-[92vh]">
      <div class="shrink-0 flex items-start justify-between gap-4 border-b border-slate-200 p-4 sm:p-5">
        <div>
          <h2 class="m-0 text-xl font-semibold text-slate-950">Choose location</h2>
          <p class="m-0 mt-1 text-sm font-medium text-slate-500">Choose a country, search on the map, or drag the marker.</p>
        </div>
        <button class="grid h-4 w-4 place-items-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-950" type="button" aria-label="Close location picker" @click="closeLocationModal">
          <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>

      <div class="grid min-h-0 flex-1 gap-4 overflow-y-auto p-4 sm:p-5">
        <div class="flex flex-wrap gap-2">
          <button class="location-chip bg-blue-50 text-blue-700" type="button" @click="useCurrentLocation">
            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
              <path d="M12 2v3" />
              <path d="M12 19v3" />
              <path d="M2 12h3" />
              <path d="M19 12h3" />
              <circle cx="12" cy="12" r="7" />
              <circle cx="12" cy="12" r="2" />
            </svg>
            Current location
          </button>
          <button
            v-for="country in countries"
            :key="country.label"
            class="location-chip"
            :class="country.class"
            type="button"
            @click="selectCountry(country)"
          >
            {{ country.label }}
          </button>
        </div>

        <form class="grid gap-2 sm:grid-cols-[1fr_auto]" @submit.prevent="searchMapLocation">
          <div class="relative">
            <input
              v-model="modalSearch"
              class="min-h-12 w-full rounded-2xl border border-slate-300 py-3 pl-4 pr-11 outline-none transition placeholder:text-slate-400 focus:border-orange-500 focus:ring-4 focus:ring-orange-100"
              placeholder="Search city, area, or address"
              autocomplete="off"
              @input="handleModalLocationSearch"
            />
            <button
              v-if="modalSearch"
              class="absolute right-3 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              type="button"
              aria-label="Clear location search"
              @click="clearModalLocationSearch"
            >
              <i class="ri-close-line text-lg" aria-hidden="true"></i>
            </button>
            <div
              v-if="modalLocationSuggestions.length || modalLocationLoading"
              class="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-20 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-950/10"
            >
              <div v-if="modalLocationLoading" class="px-4 py-3 text-sm font-medium text-slate-500">
                Searching locations...
              </div>
              <template v-else>
                <button
                  v-for="suggestion in modalLocationSuggestions"
                  :key="suggestion.id"
                  class="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-slate-50"
                  type="button"
                  @click="selectModalLocationSuggestion(suggestion)"
                >
                  <i class="ri-map-pin-line mt-0.5 text-base text-orange-600" aria-hidden="true"></i>
                  <span class="min-w-0 flex-1">
                    <span class="block truncate text-sm font-semibold text-slate-950">{{ suggestion.mainText }}</span>
                    <span class="mt-0.5 block truncate text-xs font-medium text-slate-500">{{ suggestion.secondaryText }}</span>
                  </span>
                </button>
              </template>
            </div>
          </div>
          <button class="nf-primary-button" type="submit">
            Search map
          </button>
        </form>

        <div class="overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
          <div ref="mapEl" class="h-60 w-full sm:h-72" />
          <div v-if="mapError" class="border-t border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-600">
            {{ mapError }}
          </div>
        </div>

        <div v-if="session.isLoggedIn && (recentLocationLoading || recentLocations.length)" class="grid gap-2">
          <div class="flex items-center justify-between gap-3">
            <p class="m-0 text-sm font-semibold text-slate-700">Recent searches</p>
            <span v-if="recentLocationLoading" class="text-xs font-medium text-slate-400">Loading...</span>
          </div>
          <div v-if="recentLocations.length" class="relative overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <button
              v-for="item in visibleRecentLocations"
              :key="item.id"
              class="flex w-full items-start gap-3 border-b border-slate-100 px-4 py-3 text-left transition last:border-b-0 hover:bg-blue-50/60"
              type="button"
              @click="selectRecentLocation(item)"
            >
              <span class="mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-500">
                <i class="ri-time-line text-base" aria-hidden="true"></i>
              </span>
              <span class="min-w-0 flex-1">
                <span class="block truncate text-sm font-semibold text-slate-900">{{ item.location }}</span>
                <span v-if="item.city_name || item.state_name || item.country_name" class="mt-0.5 block truncate text-xs font-medium text-slate-500">
                  {{ [item.city_name, item.state_name, item.country_name].filter(Boolean).join(', ') }}
                </span>
              </span>
              <i class="ri-arrow-right-s-line mt-1 shrink-0 text-lg text-slate-300" aria-hidden="true"></i>
            </button>
            <div v-if="hasHiddenRecentLocations" class="absolute inset-x-0 bottom-0 grid place-items-center bg-gradient-to-t from-white via-white/95 to-white/20 px-4 pb-2 pt-8">
              <button
                class="inline-flex items-center gap-1 rounded-full bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-md shadow-blue-100 transition hover:bg-blue-700"
                type="button"
                @click="showAllRecentLocations = true"
              >
                <span>Show more</span>
                <i class="ri-arrow-down-s-line text-sm" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>

      </div>

      <div class="shrink-0 border-t border-slate-200 bg-white p-4 sm:p-5">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p class="m-0 min-w-0 text-sm font-medium text-slate-500">
            Selected: <span class="text-slate-950">{{ selectedLocation || 'No location selected' }}</span>
          </p>
          <div class="flex shrink-0 gap-2">
            <button class="min-h-10 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50" type="button" @click="clearLocation">
              Clear
            </button>
            <button class="nf-primary-button" type="button" @click="applyLocation">
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineComponent, h, nextTick, onMounted, onUnmounted, ref, Teleport, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import { useSessionStore } from '@/stores/session'
import NavbarSearchLocation from '@/components/common/NavbarSearchLocation.vue'
import api from '@/services/api'
import { loadGoogleMapsAPI } from '@/utils/googleMaps'

const props = defineProps({
  desktopSidebarExpanded: { type: Boolean, default: false }
})

const emit = defineEmits(['update:desktopSidebarExpanded'])

const route = useRoute()
const router = useRouter()
const session = useSessionStore()
const search = ref('')
const mobileSidebarOpen = ref(false)
const accountDropdownOpen = ref(false)
const accountDropdownEl = ref(null)
const locationModalOpen = ref(false)
const location = ref('')
const modalSearch = ref('')
const selectedLocation = ref('')
const selectedCoords = ref(null)
const selectedLocationParts = ref({})
const selectedLocationMode = ref('country')
const modalLocationSuggestions = ref([])
const modalLocationLoading = ref(false)
const recentLocations = ref([])
const recentLocationLoading = ref(false)
const showAllRecentLocations = ref(false)
const mapEl = ref(null)
const mapError = ref('')
const exploreSections = ref([])
const searchSuggestions = ref([])
const searchLoading = ref(false)
let map
let marker
let geocoder
let autocompleteService
let modalLocationSearchTimer
let searchTimer
let searchRequestId = 0

const LOCATION_STORAGE_KEY = 'netflyr.location'

const publicLinks = [
  { label: 'Explore', to: '/explore', icon: 'home' },
  { label: 'Login', to: '/login', icon: 'login' },
  { label: 'Sign up', to: '/register', icon: 'user-plus' }
]

const privateLinks = [
  { label: 'Explore', to: '/explore', icon: 'home' }
]

const accountDropdownLinks = [
  { label: 'Account', to: '/account', icon: 'ri-user-3-line' },
  { label: 'Checkins', to: '/checkins', icon: 'ri-map-pin-user-line' },
  { label: 'Chats', to: '/chats', icon: 'ri-chat-3-line' },
  { label: 'Settings', to: '/settings', icon: 'ri-settings-3-line' },
  { label: 'Support', to: '/support', icon: 'ri-question-line' }
]

const visibleLinks = computed(() => (session.isLoggedIn ? privateLinks : publicLinks))
const accountUserType = computed(() => String(
  session.profile?.user_type || session.profile?.userType || session.user?.user_type || session.user?.userType || ''
).trim().toLowerCase())
const isBusinessAccount = computed(() => accountUserType.value === 'business')
const accountLabel = computed(() => firstValue(
  isBusinessAccount.value ? session.profile?.business_name : '',
  isBusinessAccount.value ? session.user?.business_name : '',
  session.profile?.name,
  session.profile?.user_name,
  session.user?.name,
  'Account'
))
const accountInitial = computed(() => String(accountLabel.value || 'A').trim().charAt(0).toUpperCase() || 'A')
const isExploreRoute = computed(() => route.path === '/explore')
const activeContentType = computed(() => (typeof route.query.contentType === 'string' && route.query.contentType.trim()) || 'all')
const profileImage = computed(() =>
  firstValue(
    session.profile?.image,
    session.profile?.profile_image,
    session.profile?.user_img,
    session.profile?.business_image,
    session.profile?.logo,
    session.user?.image,
    session.user?.profile_image,
    session.user?.user_img,
    ''
  )
)
const displayLocation = computed(() => {
  const parts = location.value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)

  return parts.length > 2 ? parts.slice(0, 2).join(', ') : location.value || 'Choose location'
})
const RECENT_LOCATION_LIMIT = 6
const visibleRecentLocations = computed(() =>
  showAllRecentLocations.value ? recentLocations.value : recentLocations.value.slice(0, RECENT_LOCATION_LIMIT)
)
const hasHiddenRecentLocations = computed(() => !showAllRecentLocations.value && recentLocations.value.length > RECENT_LOCATION_LIMIT)

const countries = [
  { label: 'USA', location: 'United States', center: { lat: 39.8283, lng: -98.5795 }, zoom: 4, class: 'bg-blue-50 text-blue-700' },
  { label: 'India', location: 'India', center: { lat: 20.5937, lng: 78.9629 }, zoom: 5, class: 'bg-blue-50 text-blue-700' }
]

const isCountryLocation = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  return countries.some((country) => country.location.toLowerCase() === normalized)
}

const submitSearch = () => {
  const query = search.value.trim()
  if (!query) return
  fetchSearchSuggestions(query)
  router.push({ path: '/explore', query: { search: query } })
}

const toggleDesktopSidebar = () => {
  const nextValue = !props.desktopSidebarExpanded
  emit('update:desktopSidebarExpanded', nextValue)
}

const closeAccountDropdown = () => {
  accountDropdownOpen.value = false
}

const handleAccountDropdownPointerDown = (event) => {
  if (!accountDropdownOpen.value || accountDropdownEl.value?.contains(event.target)) return
  closeAccountDropdown()
}

const handleAccountDropdownKeydown = (event) => {
  if (event.key === 'Escape') closeAccountDropdown()
}

const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== '')

const currentUserId = () => session.userId || session.user?.user_id || session.user?.userid || session.user?.uid || ''

const getAddressPart = (components = [], type = '') =>
  components.find((component) => component.types?.includes(type))?.long_name || ''

const getLocationPayload = ({ locationText = '', coords = null, result = null, country = '', city = '', state = '', address = '' } = {}) => {
  const components = result?.address_components || []
  const streetAddress = [getAddressPart(components, 'street_number'), getAddressPart(components, 'route')]
    .filter(Boolean)
    .join(' ')

  return {
    lng: coords?.lng !== undefined && coords?.lng !== null ? String(coords.lng) : '',
    userid: String(currentUserId() || ''),
    location: String(locationText || result?.formatted_address || '').trim(),
    lat: coords?.lat !== undefined && coords?.lat !== null ? String(coords.lat) : '',
    country_name: country || getAddressPart(components, 'country'),
    city_name:
      city ||
      getAddressPart(components, 'locality') ||
      getAddressPart(components, 'postal_town') ||
      getAddressPart(components, 'administrative_area_level_2'),
    address: address || streetAddress,
    state_name: state || getAddressPart(components, 'administrative_area_level_1')
  }
}

const normalizeRecentLocation = (item = {}, index = 0) => {
  const locationText = firstValue(item.location, item.address_location, item.formatted_address, item.name, '')
  const lat = firstValue(item.lat, item.latitude, '')
  const lng = firstValue(item.lng, item.long, item.longitude, '')

  return {
    id: String(firstValue(item.id, item.location_id, `${locationText}-${index}`)),
    location: String(locationText || '').trim(),
    lat: lat !== '' ? Number(lat) : null,
    lng: lng !== '' ? Number(lng) : null,
    country_name: firstValue(item.country_name, item.country, ''),
    city_name: firstValue(item.city_name, item.city, ''),
    address: firstValue(item.address, ''),
    state_name: firstValue(item.state_name, item.state, '')
  }
}

const extractRecentLocations = (payload) => {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []

  const candidates = [
    payload.list,
    payload.data,
    payload.result,
    payload.locations,
    payload.recent_locations,
    payload.data?.list,
    payload.data?.data,
    payload.data?.locations
  ]

  return candidates.find(Array.isArray) || []
}

const loadRecentLocations = async () => {
  const userid = currentUserId()
  if (!userid) {
    recentLocations.value = []
    return
  }

  recentLocationLoading.value = true
  try {
    const { data } = await api.getLocations({ userid })
    recentLocations.value = extractRecentLocations(data)
      .map(normalizeRecentLocation)
      .filter((item) => item.location)
      .slice(0, 20)
  } catch (error) {
    recentLocations.value = []
  } finally {
    recentLocationLoading.value = false
  }
}

const rememberLocationSearch = async (payload) => {
  if (!payload?.userid || !payload?.location) return

  try {
    await api.saveLocation(payload)
    await loadRecentLocations()
  } catch (error) {
    // Search should still work when history saving fails.
  }
}

const rememberSelectedLocationSearch = (payload) => {
  selectedLocationParts.value = {
    country_name: payload?.country_name || '',
    city_name: payload?.city_name || '',
    state_name: payload?.state_name || '',
    address: payload?.address || ''
  }
  rememberLocationSearch(payload)
}

const countryFromLocation = (value) => {
  const normalized = String(value || '').toLowerCase()
  if (normalized.includes('india')) return 'India'
  if (normalized.includes('united states') || normalized.includes('usa')) return 'United States'
  return 'India'
}

const buildNavbarSearchParams = (query, page = 1) => {
  const isCountry = selectedLocationMode.value === 'country' || isCountryLocation(location.value)
  const coords = !isCountry ? selectedCoords.value : null

  return {
    lat: coords?.lat ? String(coords.lat) : '',
    long: coords?.lng ? String(coords.lng) : '',
    country: isCountry ? countryFromLocation(location.value) : '',
    search_word: query,
    page
  }
}

const extractSearchRows = (payload) => {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []

  const candidates = [
    payload.list,
    payload.data,
    payload.result,
    payload.results,
    payload.flyers,
    payload.nearby_flyers,
    payload.data?.list,
    payload.data?.data,
    payload.data?.result,
    payload.data?.flyers
  ]

  return candidates.find(Array.isArray) || []
}

const buildImageUrl = (value) => {
  const image = Array.isArray(value) ? value[0] : value
  if (!image) return ''
  if (/^https?:\/\//i.test(image)) return image
  return `https://test.amplifierapp.xyz/${String(image).replace(/^\/+/, '')}`
}

const normalizeSearchSuggestion = (item = {}, index = 0) => {
  const category = firstValue(item.category_name, item.cat_name, item.category, item.content_type, 'Flyer')
  const title = firstValue(item.title, item.flyer_title, item.name, item.heading, item.description, category)
  const locationText = firstValue(item.location, item.city, item.state, item.country, item.address, '')
  const image = buildImageUrl(firstValue(item.image, item.flyer_image, item.flyer_img, item.image1, item.photo, item.thumbnail))

  return {
    id: firstValue(item.id, item.flyer_id, item.f_id, `search-${index}`),
    title,
    meta: [category, locationText].filter(Boolean).join(' • '),
    image,
    initial: String(title || category).trim().charAt(0).toUpperCase() || 'F'
  }
}

const fetchSearchSuggestions = async (query = search.value.trim()) => {
  const normalizedQuery = query.trim()
  if (normalizedQuery.length < 2) {
    searchSuggestions.value = []
    searchLoading.value = false
    return
  }

  const requestId = ++searchRequestId
  searchLoading.value = true

  try {
    const { data } = await api.searchFlyers(buildNavbarSearchParams(normalizedQuery, 1))
    if (requestId !== searchRequestId) return
    searchSuggestions.value = extractSearchRows(data).slice(0, 6).map(normalizeSearchSuggestion)
  } catch {
    if (requestId === searchRequestId) {
      searchSuggestions.value = []
    }
  } finally {
    if (requestId === searchRequestId) {
      searchLoading.value = false
    }
  }
}

const selectSearchSuggestion = (suggestion) => {
  if (!suggestion?.id) return
  searchSuggestions.value = []
  router.push(`/flyers/${suggestion.id}`)
}

const normalizeHexColor = (value, fallback) => {
  const normalized = String(value || fallback).replace('#', '').trim()
  return /^[0-9a-f]{6}$/i.test(normalized) ? normalized : fallback
}

const extractSections = (payload) => {
  if (!payload || typeof payload !== 'object') return []

  const candidates = [
    payload.sections,
    payload.data?.sections,
    payload.list,
    payload.data?.list,
    payload.result,
    payload.data?.result
  ]

  const source = candidates.find((candidate) => Array.isArray(candidate))
  if (!source) return []

  return source.map((section, sectionIndex) => ({
    id: section.id || section.section_id || section.name || `section-${sectionIndex}`,
    label: section.name || section.section_name || section.label || 'Browse',
    description: section.description || '',
    headingColor: `#${normalizeHexColor(firstValue(section.color_code, section.colorCode, '475569'), '475569')}`,
    items: firstValue(section.inner_list, section.content_types, section.types, section.items, section.children, [])
      .map((item) => ({
        id: String(firstValue(item.id, item.content_type_id, item.value, '')),
        label: firstValue(item.name, item.label, item.title, 'Unknown'),
        description: firstValue(item.description, ''),
        icon: resolveContentTypeIcon(firstValue(item.name, item.label, item.title, 'Unknown')),
        colorCode: normalizeHexColor(firstValue(item.color_code, item.colorCode, '64748b'), '64748b')
      }))
      .filter((item) => item.id)
  })).filter((section) => section.items.length)
}

const fetchExploreSections = async () => {
  try {
    const { data } = await api.getFlyerContentTypesSections()
    exploreSections.value = extractSections(data)
  } catch {
    exploreSections.value = []
  }
}

const contentTypeIconRules = [
  { keys: ['offer', 'coupon', 'deal', 'discount', 'promo', 'promotion'], line: 'ri-coupon-3-line', fill: 'ri-coupon-3-fill' },
  { keys: ['market place', 'marketplace', 'market', 'shop', 'store', 'buy', 'sell'], line: 'ri-shopping-bag-3-line', fill: 'ri-shopping-bag-3-fill' },
  { keys: ['event', 'party', 'festival', 'meetup', 'concert', 'show'], line: 'ri-calendar-event-line', fill: 'ri-calendar-event-fill' },
  { keys: ['inspiration', 'idea', 'tips', 'guide', 'learning'], line: 'ri-lightbulb-flash-line', fill: 'ri-lightbulb-flash-fill' },
  { keys: ['menu', 'menus', 'restaurant', 'cafe', 'food', 'meal', 'dining'], line: 'ri-restaurant-2-line', fill: 'ri-restaurant-2-fill' },
  { keys: ['announcement', 'notice', 'news', 'update', 'community'], line: 'ri-megaphone-line', fill: 'ri-megaphone-fill' },
  { keys: ['request', 'requests', 'needed', 'wanted', 'ask'], line: 'ri-question-answer-line', fill: 'ri-question-answer-fill' },
  { keys: ['contact', 'contacts', 'directory', 'phone'], line: 'ri-contacts-book-2-line', fill: 'ri-contacts-book-2-fill' },
  { keys: ['job', 'career', 'work', 'hiring', 'employment'], line: 'ri-briefcase-4-line', fill: 'ri-briefcase-4-fill' },
  { keys: ['review', 'rating', 'testimonial', 'feedback'], line: 'ri-star-smile-line', fill: 'ri-star-smile-fill' },
  { keys: ['alert', 'warning', 'urgent', 'safety'], line: 'ri-alarm-warning-line', fill: 'ri-alarm-warning-fill' },
  { keys: ['property', 'housing', 'house', 'home', 'room', 'rent', 'real estate'], line: 'ri-home-4-line', fill: 'ri-home-4-fill' },
  { keys: ['ride', 'car', 'travel', 'transport', 'pickup'], line: 'ri-car-line', fill: 'ri-car-fill' },
  { keys: ['service', 'repair', 'professional', 'maintenance'], line: 'ri-tools-line', fill: 'ri-tools-fill' },
  { keys: ['class', 'course', 'lesson', 'education', 'tuition'], line: 'ri-graduation-cap-line', fill: 'ri-graduation-cap-fill' },
  { keys: ['health', 'fitness', 'medical', 'wellness', 'yoga'], line: 'ri-heart-pulse-line', fill: 'ri-heart-pulse-fill' },
  { keys: ['lost', 'found', 'missing'], line: 'ri-search-eye-line', fill: 'ri-search-eye-fill' },
  { keys: ['flyer', 'post', 'general'], line: 'ri-file-list-3-line', fill: 'ri-file-list-3-fill' }
]

const resolveContentTypeIcon = (label) => {
  const normalized = String(label || '').toLowerCase()
  return contentTypeIconRules.find((rule) => rule.keys.some((key) => normalized.includes(key))) || {
    line: 'ri-file-list-3-line',
    fill: 'ri-file-list-3-fill'
  }
}

const logout = async () => {
  const result = await Swal.fire({
    title: 'Logout?',
    text: 'You will need to login again to access your account.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Logout',
    cancelButtonText: 'Cancel',
    reverseButtons: true,
    confirmButtonColor: '#5b21b6',
    cancelButtonColor: '#cbd5e1',
    background: '#ffffff',
    color: '#0f172a'
  })

  if (!result.isConfirmed) return

  closeAccountDropdown()
  session.logout()
  mobileSidebarOpen.value = false
  router.push('/explore')
}

const inferCurrentCountry = () => {
  const locale = navigator.language || ''
  const region = locale.includes('-') ? locale.split('-').pop() : ''

  if (region && typeof Intl !== 'undefined' && Intl.DisplayNames) {
    const displayNames = new Intl.DisplayNames([locale], { type: 'region' })
    const country = displayNames.of(region) || ''
    if (country === 'India' || country === 'United States') return country
  }

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  if (timeZone.includes('Kolkata')) return 'India'
  if (timeZone.includes('New_York') || timeZone.includes('Chicago') || timeZone.includes('Denver') || timeZone.includes('Los_Angeles')) return 'United States'
  return 'India'
}

const saveLocation = (value, coords = null, mode = coords ? 'location' : 'country', parts = {}) => {
  location.value = value
  selectedCoords.value = coords
  selectedLocationMode.value = mode
  const payload = {
    location: value,
    coords: mode === 'country' ? null : coords,
    mode,
    country_name: parts.country_name || '',
    city_name: parts.city_name || '',
    state_name: parts.state_name || '',
    address: parts.address || ''
  }
  localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(payload))
  window.dispatchEvent(new CustomEvent('netflyr-location-change', { detail: payload }))
}

const loadSavedLocation = () => {
  const saved = localStorage.getItem(LOCATION_STORAGE_KEY)
  if (saved) {
    try {
      const parsed = JSON.parse(saved)
      if (parsed?.location) {
        const mode = parsed.mode || (isCountryLocation(parsed.location) ? 'country' : parsed.coords ? 'location' : 'country')
        location.value = parsed.location
        selectedCoords.value = mode === 'country' ? null : parsed.coords || null
        selectedLocationMode.value = mode
        selectedLocationParts.value = {
          country_name: parsed.country_name || '',
          city_name: parsed.city_name || '',
          state_name: parsed.state_name || '',
          address: parsed.address || ''
        }
        return
      }
    } catch (error) {
      localStorage.removeItem(LOCATION_STORAGE_KEY)
    }
  }

  const country = inferCurrentCountry()
  saveLocation(country, null, 'country')
}

const openLocationModal = async () => {
  selectedLocation.value = location.value
  modalSearch.value = location.value
  showAllRecentLocations.value = false
  locationModalOpen.value = true
  loadRecentLocations()
  await nextTick()
  initMap()
}

const closeLocationModal = () => {
  locationModalOpen.value = false
  map = null
  marker = null
  geocoder = null
  autocompleteService = null
  mapError.value = ''
  modalLocationSuggestions.value = []
  modalLocationLoading.value = false
  if (modalLocationSearchTimer) {
    clearTimeout(modalLocationSearchTimer)
    modalLocationSearchTimer = null
  }
}

const initMap = async () => {
  if (!mapEl.value || map) return

  try {
    await loadGoogleMapsAPI()
    geocoder = new window.google.maps.Geocoder()
    autocompleteService = new window.google.maps.places.AutocompleteService()
    const center = selectedCoords.value || countries.find((country) => country.location === location.value)?.center || countries[0].center
    map = new window.google.maps.Map(mapEl.value, {
      center,
      zoom: selectedCoords.value ? 11 : 4,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false
    })
    marker = new window.google.maps.Marker({ position: center, map, draggable: true })
    marker.addListener('dragend', () => {
      const position = marker.getPosition()
      if (position) reverseGeocode({ lat: position.lat(), lng: position.lng() })
    })
    map.addListener('click', (event) => {
      const position = { lat: event.latLng.lat(), lng: event.latLng.lng() }
      setMapPosition(position, map.getZoom())
      reverseGeocode(position)
    })
  } catch (error) {
    mapError.value = 'Google Map could not load here. You can still search or choose a country.'
  }
}

const normalizeLocationPrediction = (prediction, index) => ({
  id: prediction.place_id || `location-${index}`,
  placeId: prediction.place_id || '',
  mainText: prediction.structured_formatting?.main_text || prediction.description,
  secondaryText: prediction.structured_formatting?.secondary_text || '',
  description: prediction.description || prediction.structured_formatting?.main_text || ''
})

const fetchModalLocationSuggestions = () => {
  const query = modalSearch.value.trim()
  mapError.value = ''

  if (!query || query.length < 2) {
    modalLocationSuggestions.value = []
    modalLocationLoading.value = false
    return
  }

  if (!autocompleteService) {
    modalLocationSuggestions.value = []
    modalLocationLoading.value = false
    return
  }

  modalLocationLoading.value = true
  autocompleteService.getPlacePredictions(
    {
      input: query,
      types: ['geocode']
    },
    (predictions, status) => {
      modalLocationLoading.value = false
      if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions?.length) {
        modalLocationSuggestions.value = predictions.map(normalizeLocationPrediction)
        return
      }

      modalLocationSuggestions.value = []
      if (status && status !== window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
        mapError.value = 'Location suggestions could not be loaded. You can still search or apply the typed location.'
      }
    }
  )
}

const handleModalLocationSearch = () => {
  selectedLocation.value = modalSearch.value.trim()
  selectedLocationMode.value = 'location'
  modalLocationLoading.value = modalSearch.value.trim().length >= 2

  if (modalLocationSearchTimer) clearTimeout(modalLocationSearchTimer)
  modalLocationSearchTimer = setTimeout(fetchModalLocationSuggestions, 250)
}

const clearModalLocationSearch = () => {
  modalSearch.value = ''
  selectedLocation.value = ''
  selectedCoords.value = null
  selectedLocationMode.value = 'location'
  modalLocationSuggestions.value = []
  modalLocationLoading.value = false
  mapError.value = ''

  if (modalLocationSearchTimer) {
    clearTimeout(modalLocationSearchTimer)
    modalLocationSearchTimer = null
  }
}

const selectModalLocationSuggestion = (suggestion) => {
  modalLocationSuggestions.value = []
  modalLocationLoading.value = false
  modalSearch.value = suggestion.description
  selectedLocation.value = suggestion.description
  selectedLocationMode.value = 'location'

  if (!geocoder) {
    selectedCoords.value = null
    rememberSelectedLocationSearch(getLocationPayload({ locationText: selectedLocation.value }))
    return
  }

  const request = suggestion.placeId ? { placeId: suggestion.placeId } : { address: suggestion.description }
  geocoder.geocode(request, (results, status) => {
    if (status === 'OK' && results?.[0]) {
      const resultLocation = results[0].geometry.location
      const position = { lat: resultLocation.lat(), lng: resultLocation.lng() }
      selectedLocation.value = results[0].formatted_address || suggestion.description
      modalSearch.value = selectedLocation.value
      setMapPosition(position, 12)
      rememberSelectedLocationSearch(getLocationPayload({ locationText: selectedLocation.value, coords: position, result: results[0] }))
      return
    }

    selectedCoords.value = null
  })
}

const setMapPosition = (position, zoom = 11) => {
  selectedCoords.value = position
  if (!map || !marker) return
  map.setCenter(position)
  map.setZoom(zoom)
  marker.setPosition(position)
}

const selectCountry = (country) => {
  selectedLocation.value = country.location
  modalSearch.value = country.location
  modalLocationSuggestions.value = []
  modalLocationLoading.value = false
  selectedLocationMode.value = 'country'
  setMapPosition(country.center, country.zoom)
  rememberSelectedLocationSearch(
    getLocationPayload({
      locationText: country.location,
      coords: country.center,
      country: country.location
    })
  )
}

const useCurrentLocation = () => {
  if (!navigator.geolocation) {
    mapError.value = 'Current location is not available in this browser.'
    return
  }

  navigator.geolocation.getCurrentPosition(
    (position) => {
      const coords = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      }
      setMapPosition(coords, 13)
      selectedLocationMode.value = 'location'
      reverseGeocode(coords)
    },
    () => {
      mapError.value = 'Location permission was not granted. Search for a city or choose a country.'
    }
  )
}

const reverseGeocode = (position) => {
  selectedCoords.value = position
  selectedLocationMode.value = 'location'
  modalLocationSuggestions.value = []
  modalLocationLoading.value = false
  if (!geocoder) {
    selectedLocation.value = `${position.lat.toFixed(4)}, ${position.lng.toFixed(4)}`
    return
  }

  geocoder.geocode({ location: position }, (results, status) => {
    if (status === 'OK' && results?.[0]) {
      selectedLocation.value = results[0].formatted_address
      modalSearch.value = results[0].formatted_address
      rememberSelectedLocationSearch(getLocationPayload({ locationText: selectedLocation.value, coords: position, result: results[0] }))
    } else {
      selectedLocation.value = `${position.lat.toFixed(4)}, ${position.lng.toFixed(4)}`
      rememberSelectedLocationSearch(getLocationPayload({ locationText: selectedLocation.value, coords: position }))
    }
  })
}

const searchMapLocation = () => {
  if (!modalSearch.value.trim()) return
  modalLocationSuggestions.value = []
  modalLocationLoading.value = false

  if (!geocoder) {
    selectedLocation.value = modalSearch.value.trim()
    selectedCoords.value = null
    selectedLocationMode.value = 'location'
    rememberSelectedLocationSearch(getLocationPayload({ locationText: selectedLocation.value }))
    return
  }

  geocoder.geocode({ address: modalSearch.value.trim() }, (results, status) => {
    if (status === 'OK' && results?.[0]) {
      const resultLocation = results[0].geometry.location
      const position = { lat: resultLocation.lat(), lng: resultLocation.lng() }
      selectedLocation.value = results[0].formatted_address
      selectedLocationMode.value = 'location'
      setMapPosition(position, 12)
      rememberSelectedLocationSearch(getLocationPayload({ locationText: selectedLocation.value, coords: position, result: results[0] }))
    } else {
      selectedLocation.value = modalSearch.value.trim()
      selectedCoords.value = null
      selectedLocationMode.value = 'location'
      mapError.value = 'Map search did not find an exact match, but the typed location can still be applied.'
      rememberSelectedLocationSearch(getLocationPayload({ locationText: selectedLocation.value }))
    }
  })
}

const selectRecentLocation = (item) => {
  const coords = Number.isFinite(item.lat) && Number.isFinite(item.lng) ? { lat: item.lat, lng: item.lng } : null
  selectedLocation.value = item.location
  modalSearch.value = item.location
  selectedCoords.value = coords
  selectedLocationMode.value = isCountryLocation(item.location) ? 'country' : 'location'
  modalLocationSuggestions.value = []
  modalLocationLoading.value = false
  if (coords) setMapPosition(coords, selectedLocationMode.value === 'country' ? 5 : 12)
  rememberSelectedLocationSearch(
    getLocationPayload({
      locationText: item.location,
      coords,
      country: item.country_name,
      city: item.city_name,
      address: item.address,
      state: item.state_name
    })
  )
}

const clearLocation = () => {
  localStorage.removeItem(LOCATION_STORAGE_KEY)
  location.value = ''
  selectedLocation.value = ''
  selectedCoords.value = null
  selectedLocationParts.value = {}
  selectedLocationMode.value = 'country'
  modalSearch.value = ''
  modalLocationSuggestions.value = []
  modalLocationLoading.value = false
  window.dispatchEvent(new CustomEvent('netflyr-location-change', { detail: null }))
  closeLocationModal()
}

const applyLocation = () => {
  const nextLocation = selectedLocation.value || modalSearch.value.trim()
  if (nextLocation) {
    const isCountry = selectedLocationMode.value === 'country'
    const parts = {
      ...selectedLocationParts.value,
      country_name: isCountry ? nextLocation : selectedLocationParts.value.country_name || ''
    }
    saveLocation(nextLocation, isCountry ? null : selectedCoords.value, isCountry ? 'country' : 'location', parts)
    rememberSelectedLocationSearch(
      getLocationPayload({
        locationText: nextLocation,
        coords: selectedCoords.value,
        country: isCountry ? nextLocation : ''
      })
    )
  }
  closeLocationModal()
}

onMounted(() => {
  loadSavedLocation()
  fetchExploreSections()
  if (session.isLoggedIn && !session.profile) session.fetchProfile()
  window.addEventListener('netflyr-open-location-picker', openLocationModal)
  document.addEventListener('pointerdown', handleAccountDropdownPointerDown)
  document.addEventListener('keydown', handleAccountDropdownKeydown)
})

watch(() => route.fullPath, closeAccountDropdown)

watch(isExploreRoute, (value) => {
  if (value && !exploreSections.value.length) {
    fetchExploreSections()
  }
}, { immediate: true })

watch(search, (value) => {
  clearTimeout(searchTimer)
  const query = value.trim()
  if (query.length < 2) {
    searchSuggestions.value = []
    searchLoading.value = false
    return
  }
  searchTimer = setTimeout(() => {
    fetchSearchSuggestions(query)
  }, 250)
})

onUnmounted(() => {
  clearTimeout(searchTimer)
  window.removeEventListener('netflyr-open-location-picker', openLocationModal)
  document.removeEventListener('pointerdown', handleAccountDropdownPointerDown)
  document.removeEventListener('keydown', handleAccountDropdownKeydown)
})

const remixMenuIcons = {
  home: { line: 'ri-home-5-line', fill: 'ri-home-5-fill' },
  login: { line: 'ri-login-circle-line', fill: 'ri-login-circle-fill' },
  'user-plus': { line: 'ri-user-add-line', fill: 'ri-user-add-fill' },
  plus: { line: 'ri-add-line', fill: 'ri-add-fill' },
  stack: { line: 'ri-stack-line', fill: 'ri-stack-fill' },
  bookmark: { line: 'ri-bookmark-line', fill: 'ri-bookmark-fill' },
  chat: { line: 'ri-chat-3-line', fill: 'ri-chat-3-fill' },
  user: { line: 'ri-user-3-line', fill: 'ri-user-3-fill' },
  settings: { line: 'ri-settings-3-line', fill: 'ri-settings-3-fill' },
  help: { line: 'ri-question-line', fill: 'ri-question-fill' }
}

const sidebarIconStyle = Object.freeze({
  width: '1.25rem',
  height: '1.25rem',
  fontSize: '1.25rem',
  lineHeight: '1'
})

const brandIconPaths = {
  xTwitter: 'M13.9 10.5 22.6 0h-2.1l-7.6 9.1L6.9 0H0l9.1 13.3L0 24h2.1l8-9.4 6.4 9.4h6.9l-9.5-13.5Zm-2.8 3.3-.9-1.3L2.9 1.6h3l5.9 8.7.9 1.3 7.7 11.1h-3l-6.3-8.9Z',
  facebook: 'M24 12.1C24 5.4 18.6 0 12 0S0 5.4 0 12.1C0 18.1 4.4 23.1 10.1 24v-8.4h-3V12h3V9.3c0-3 1.8-4.7 4.5-4.7 1.3 0 2.7.2 2.7.2v3h-1.5c-1.5 0-2 .9-2 1.9V12h3.4l-.5 3.6h-2.9V24C19.6 23.1 24 18.1 24 12.1Z',
  youtube: 'M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31.4 31.4 0 0 0 0 12a31.4 31.4 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31.4 31.4 0 0 0 24 12a31.4 31.4 0 0 0-.5-5.8ZM9.6 15.6V8.4l6.3 3.6-6.3 3.6Z',
  instagram: 'M12 2.2c3.2 0 3.6 0 4.9.1 1.2.1 1.9.3 2.3.5.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.4 1.1.5 2.3.1 1.3.1 1.7.1 4.9s0 3.6-.1 4.9c-.1 1.2-.3 1.9-.5 2.3-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1.1.4-2.3.5-1.3.1-1.7.1-4.9.1s-3.6 0-4.9-.1c-1.2-.1-1.9-.3-2.3-.5-.6-.2-1-.5-1.4-.9-.4-.4-.7-.8-.9-1.4-.2-.4-.4-1.1-.5-2.3-.1-1.3-.1-1.7-.1-4.9s0-3.6.1-4.9c.1-1.2.3-1.9.5-2.3.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1.1-.4 2.3-.5 1.3-.1 1.7-.1 4.9-.1ZM12 0C8.7 0 8.3 0 7 .1 5.7.2 4.8.4 4 .7c-.8.3-1.5.7-2.2 1.4C1.1 2.8.6 3.5.3 4.3.1 5.1-.1 6 0 7.3 0 8.6 0 9 0 12s0 3.4.1 4.7c.1 1.3.3 2.2.6 3 .3.8.7 1.5 1.4 2.2.7.7 1.4 1.1 2.2 1.4.8.3 1.7.5 3 .6 1.3.1 1.7.1 4.7.1s3.4 0 4.7-.1c1.3-.1 2.2-.3 3-.6.8-.3 1.5-.7 2.2-1.4.7-.7 1.1-1.4 1.4-2.2.3-.8.5-1.7.6-3 .1-1.3.1-1.7.1-4.7s0-3.4-.1-4.7c-.1-1.3-.3-2.2-.6-3-.3-.8-.7-1.5-1.4-2.2-.7-.7-1.4-1.1-2.2-1.4-.8-.3-1.7-.5-3-.6C15.4 0 15 0 12 0Zm0 5.8a6.2 6.2 0 1 0 0 12.4 6.2 6.2 0 0 0 0-12.4Zm0 10.2a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm7.9-10.5a1.4 1.4 0 1 1-2.8 0 1.4 1.4 0 0 1 2.8 0Z'
}

const brandIconStyles = {
  xTwitter: { color: '#0f172a', backgroundColor: '#f1f5f9' },
  facebook: { color: '#1877f2', backgroundColor: 'rgba(24, 119, 242, 0.1)' },
  youtube: { color: '#ff0000', backgroundColor: 'rgba(255, 0, 0, 0.1)' },
  instagram: { color: '#e4405f', backgroundColor: 'rgba(228, 64, 95, 0.1)' }
}

const MenuIcon = defineComponent({
  props: {
    name: { type: String, required: true },
    filled: { type: Boolean, default: false }
  },
  setup(props) {
    return () => {
      const icon = remixMenuIcons[props.name] || remixMenuIcons.home
      return h('i', {
        class: ['sidebar-remix-icon', props.filled ? icon.fill : icon.line],
        style: sidebarIconStyle,
        'aria-hidden': 'true'
      })
    }
  }
})

const SidebarContent = defineComponent({
  props: {
    links: { type: Array, required: true },
    accountLabel: { type: String, default: 'Account' },
    isLoggedIn: { type: Boolean, default: false },
    mobile: { type: Boolean, default: false },
    collapsed: { type: Boolean, default: false },
    isExploreRoute: { type: Boolean, default: false },
    exploreSections: { type: Array, default: () => [] },
    activeContentType: { type: String, default: 'all' }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const contentTypesExpanded = ref(false)
    const sidebarTooltip = ref(null)
    const CONTENT_TYPE_PREVIEW_LIMIT = 5

    const showSidebarTooltip = (label, event) => {
      if (!props.collapsed || props.mobile) return
      const rect = event.currentTarget?.getBoundingClientRect()
      if (!rect) return
      sidebarTooltip.value = {
        label,
        top: rect.top + rect.height / 2
      }
    }

    const hideSidebarTooltip = () => {
      sidebarTooltip.value = null
    }

    const isMenuLinkActive = (item) => route.path === item.to

    const renderMenuLink = (item) => {
      const active = isMenuLinkActive(item)
      return h(
        RouterLink,
        {
          key: item.to,
          to: item.to,
          class: [
            'group relative flex h-10 items-center rounded-[10px] text-sm font-normal leading-5 text-slate-900 transition hover:bg-slate-100',
            props.collapsed && !props.mobile ? 'w-12 justify-center px-0' : 'gap-5 px-3',
          ],
          activeClass: 'sidebar-link-active bg-slate-100 text-slate-950 hover:bg-slate-100',
          title: props.collapsed && !props.mobile ? item.label : null,
          onMouseenter: (event) => showSidebarTooltip(item.label, event),
          onFocus: (event) => showSidebarTooltip(item.label, event),
          onMouseleave: hideSidebarTooltip,
          onBlur: hideSidebarTooltip,
          onClick: () => {
            hideSidebarTooltip()
            emit('close')
          }
        },
        () => [
          h('span', { class: ['sidebar-menu-icon inline-flex h-10 w-10 shrink-0 items-center justify-center text-slate-900 transition', active ? 'sidebar-brand-icon' : ''] }, [
            h(MenuIcon, { name: item.icon, filled: active })
          ]),
          !props.collapsed || props.mobile ? h('span', { class: 'truncate' }, item.label) : null,
          props.collapsed && !props.mobile
            ? h(
                'span',
                {
                  class: 'pointer-events-none absolute left-[calc(100%+0.75rem)] top-1/2 z-10 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-950 px-2.5 py-1.5 text-xs font-bold text-white shadow-lg group-hover:block'
                },
                item.label
              )
            : null
        ]
      )
    }

    const renderContentTypeLink = (item) =>
      h(
        RouterLink,
        {
          key: `content-type-${item.id}`,
          to: { path: '/explore', query: item.id === 'all' ? {} : { ...route.query, contentType: item.id } },
          class: [
            'group relative flex h-10 items-center rounded-[10px] text-sm font-normal leading-5 text-slate-900 transition hover:bg-slate-100',
            props.collapsed && !props.mobile ? 'w-12 justify-center px-0' : 'gap-5 px-3',
            props.activeContentType === item.id ? 'bg-slate-100 text-slate-950' : ''
          ],
          title: props.collapsed && !props.mobile ? item.label : null,
          onMouseenter: (event) => showSidebarTooltip(item.label, event),
          onFocus: (event) => showSidebarTooltip(item.label, event),
          onMouseleave: hideSidebarTooltip,
          onBlur: hideSidebarTooltip,
          onClick: () => {
            hideSidebarTooltip()
            emit('close')
          }
        },
        () => [
          h('span', { class: ['sidebar-menu-icon inline-flex h-10 w-10 shrink-0 items-center justify-center text-slate-900 transition', props.activeContentType === item.id ? 'sidebar-brand-icon' : ''] }, [
            h('i', { class: ['sidebar-remix-icon', props.activeContentType === item.id ? item.icon.fill : item.icon.line], style: sidebarIconStyle, 'aria-hidden': 'true' })
          ]),
          !props.collapsed || props.mobile
            ? h('span', { class: 'block min-w-0 truncate' }, item.label)
            : h(
                'span',
                {
                  class: 'pointer-events-none absolute left-[calc(100%+0.75rem)] top-1/2 z-10 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-950 px-2.5 py-1.5 text-xs font-bold text-white shadow-lg group-hover:block'
                },
                item.label
              )
        ]
      )

    const renderContentTypesToggle = (remainingCount) =>
      h(
        'button',
        {
          class: [
            'group relative flex h-8 items-center border border-blue-100 bg-blue-50 text-xs font-semibold leading-4 text-blue-700 shadow-sm shadow-blue-100/60 transition hover:border-blue-200 hover:bg-blue-100 hover:text-blue-800',
            props.collapsed && !props.mobile ? 'w-8 justify-center rounded-full px-0' : 'gap-2 rounded-full px-2.5'
          ],
          type: 'button',
          onMouseenter: (event) => showSidebarTooltip(contentTypesExpanded.value ? 'Show less' : `Show more (${remainingCount})`, event),
          onFocus: (event) => showSidebarTooltip(contentTypesExpanded.value ? 'Show less' : `Show more (${remainingCount})`, event),
          onMouseleave: hideSidebarTooltip,
          onBlur: hideSidebarTooltip,
          onClick: () => {
            hideSidebarTooltip()
            contentTypesExpanded.value = !contentTypesExpanded.value
          }
        },
        [
          h('span', { class: 'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/80 text-blue-700 transition group-hover:bg-white' }, [
            h('svg', { class: ['h-4 w-4 transition', contentTypesExpanded.value ? 'rotate-180' : ''], viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2.2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true' }, [
              h('path', { d: 'm6 9 6 6 6-6' })
            ])
          ]),
          !props.collapsed || props.mobile
            ? h('span', { class: 'min-w-0 truncate pr-0.5' }, contentTypesExpanded.value ? 'Show less' : `Show more (${remainingCount})`)
            : h(
                'span',
                {
                  class: 'pointer-events-none absolute left-[calc(100%+0.75rem)] top-1/2 z-10 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-950 px-2.5 py-1.5 text-xs font-bold text-white shadow-lg group-hover:block'
                },
                contentTypesExpanded.value ? 'Show less' : `Show more (${remainingCount})`
              )
        ]
      )

    const renderContentTypes = () => {
      if (!props.exploreSections.length) return []
      const items = props.exploreSections.flatMap((section) => section.items)
      const visibleItems = contentTypesExpanded.value ? items : items.slice(0, CONTENT_TYPE_PREVIEW_LIMIT)
      const nodes = visibleItems.map(renderContentTypeLink)
      const remainingCount = items.length - CONTENT_TYPE_PREVIEW_LIMIT
      if (remainingCount > 0) {
        nodes.push(renderContentTypesToggle(remainingCount))
      }
      return nodes
    }

    return () =>
      {
        const [exploreLink, ...remainingLinks] = props.links
        const menuNodes = [
          exploreLink ? renderMenuLink(exploreLink) : null,
          ...renderContentTypes(),
          ...remainingLinks.map(renderMenuLink)
        ].filter(Boolean)

        return (
      h('div', { class: ['sidebar-scroll flex h-full flex-col overflow-y-auto px-3 py-2', props.collapsed && !props.mobile ? 'items-center px-2' : ''] }, [
        props.mobile &&
          h('div', { class: 'mb-2 flex items-center justify-between gap-2' }, [
            h('span', { class: 'px-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400' }, 'Menu'),
            h(
              'button',
              {
                class: 'grid h-4 w-4 place-items-center rounded-full text-slate-600 transition hover:bg-slate-100 hover:text-slate-950',
                type: 'button',
                'aria-label': 'Close menu',
                onClick: () => emit('close')
              },
              [
                h('svg', { class: 'h-5 w-5', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2.4', 'stroke-linecap': 'round', 'aria-hidden': 'true' }, [
                  h('path', { d: 'M18 6 6 18' }),
                  h('path', { d: 'M6 6l12 12' })
                ])
              ]
            )
          ]),
        h(
          'nav',
          { class: ['grid gap-0.5 pt-1', props.collapsed && !props.mobile ? 'w-full justify-items-center' : ''], 'aria-label': 'Menu links' },
          menuNodes
        ),
        h('div', { class: ['mt-auto grid gap-3 border-t border-slate-100 pt-3', props.collapsed && !props.mobile ? 'w-full justify-items-center' : ''] }, [
          h('div', { class: ['grid gap-2 text-xs leading-5 text-slate-500', props.collapsed && !props.mobile ? 'justify-items-center px-0' : 'px-2'] }, [
            h('div', { class: ['flex gap-2', props.collapsed && !props.mobile ? 'flex-col' : ''] }, ['xTwitter', 'facebook', 'youtube', 'instagram'].map((name) =>
              h('span', { class: 'grid h-8 w-8 place-items-center rounded-full transition hover:scale-105', style: brandIconStyles[name] }, [
                h('svg', { class: 'h-4 w-4', viewBox: '0 0 24 24', fill: 'currentColor', 'aria-hidden': 'true' }, [
                  h('path', { d: brandIconPaths[name] })
                ])
              ])
            )),
            !props.collapsed || props.mobile ? h('p', { class: 'm-0' }, 'Discover local flyers') : null,
            !props.collapsed || props.mobile
              ? h('div', { class: 'flex flex-wrap items-center gap-x-3 gap-y-1' }, [
                  h(
                    RouterLink,
                    {
                      class: 'font-semibold text-slate-500 transition hover:text-blue-700',
                      to: '/privacy-policy',
                      onClick: () => emit('close')
                    },
                    () => 'Privacy Policy'
                  ),
                  h(
                    RouterLink,
                    {
                      class: 'font-semibold text-slate-500 transition hover:text-blue-700',
                      to: '/terms-conditions',
                      onClick: () => emit('close')
                    },
                    () => 'Terms & Conditions'
                  )
                ])
              : null,
            !props.collapsed || props.mobile ? h('p', { class: 'm-0' }, 'NetFlyr © 2026. All rights reserved.') : null
          ])
        ]),
        h(Teleport, { to: 'body' }, [
          sidebarTooltip.value && props.collapsed && !props.mobile
            ? h('div', {
                class: 'pointer-events-none fixed z-[9999] -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-950 px-2.5 py-1.5 text-xs font-bold text-white shadow-xl shadow-slate-950/20',
                style: { left: '4.75rem', top: `${sidebarTooltip.value.top}px` }
              }, [
                h('span', { class: 'absolute left-[-0.25rem] top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 bg-slate-950' }),
                sidebarTooltip.value.label
              ])
            : null
        ])
      ])
        )
      }
  }
})
</script>

<style scoped>
.sidebar-scroll {
  overflow-x: visible;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.sidebar-scroll::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.sidebar-link-active {
  font-weight: 400;
}

.sidebar-remix-icon {
  display: inline-flex;
  width: 2rem !important;
  height: 2rem !important;
  align-items: center;
  justify-content: center;
  font-size: 2rem !important;
  line-height: 1 !important;
}

.sidebar-remix-icon::before {
  font-size: inherit !important;
  line-height: inherit !important;
}

.sidebar-brand-icon {
  color: var(--nf-primary) !important;
}

.location-chip {
  display: inline-flex;
  min-height: 2.25rem;
  align-items: center;
  gap: 0.375rem;
  border-radius: 9999px;
  padding: 0.5rem 0.875rem;
  font-size: 0.875rem;
  font-weight: 600;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.location-chip:hover {
  filter: brightness(0.98);
}
</style>
